import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

function safeText(s) {
  return String(s ?? "")
    .replace(/₹/g, "Rs ")
    .replace(/[·•—–]/g, "-")
    .replace(/[^\x20-\x7E\n]/g, "");
}

function drawBars(doc, { x, y, w, h, labels, values, title, color = [15, 118, 110] }) {
  const nums = (values || []).map((v) => Number(v) || 0);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(30, 41, 59);
  doc.text(safeText(title), x, y);
  if (!nums.length || nums.every((v) => v === 0)) {
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text("No data", x, y + 8);
    return y + 14;
  }
  const chartTop = y + 4;
  const chartH = h - 12;
  const max = Math.max(...nums, 1);
  const n = nums.length;
  const gap = 2;
  const barW = Math.max(4, (w - gap * (n + 1)) / n);
  nums.forEach((v, i) => {
    const bh = (v / max) * (chartH - 6);
    const bx = x + gap + i * (barW + gap);
    const by = chartTop + chartH - bh;
    doc.setFillColor(...color);
    doc.rect(bx, by, barW, Math.max(bh, 0.4), "F");
    doc.setFontSize(5.5);
    doc.setTextColor(30, 41, 59);
    doc.text(String(Math.round(v)), bx + barW / 2, by - 1, { align: "center" });
    doc.setTextColor(100, 116, 139);
    const tick = String(labels[i] || "").slice(0, 6);
    doc.text(safeText(tick), bx + barW / 2, chartTop + chartH + 3.5, { align: "center" });
  });
  return chartTop + chartH + 8;
}

export function exportEmployeeDashboardPdf({
  metrics = {},
  masters = [],
  alerts = [],
  movement = null,
  tenure = [],
  compare = [],
  periodLabel = "",
} = {}) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  doc.setFillColor(15, 118, 110);
  doc.rect(0, 0, 297, 16, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text("Workforce Dashboard", 14, 10);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(safeText(`${periodLabel || metrics.periodLabel || ""} | ${new Date().toLocaleString("en-IN")}`), 14, 14);

  autoTable(doc, {
    startY: 22,
    head: [["Metric", "Value"]],
    body: [
      ["Headcount", metrics.headcount],
      ["Active", metrics.active],
      ["New joinees", metrics.newJoinees],
      ["Join rate %", metrics.newJoineeRate],
      ["Attrition", metrics.attrition],
      ["Attrition rate %", metrics.attritionRate],
      ["Men", metrics.male],
      ["Women", metrics.female],
      ["M:F ratio", metrics.genderRatio],
      ["Probation", metrics.probation],
      ["Confirmed", metrics.confirmed],
    ],
    styles: { fontSize: 7, font: "helvetica" },
    headStyles: { fillColor: [15, 118, 110], textColor: 255 },
    margin: { left: 14, right: 160 },
    tableWidth: 110,
  });

  autoTable(doc, {
    startY: 22,
    head: [["Master", "Active HC"]],
    body: (masters || []).slice(0, 20).map((m) => [safeText(m.label), m.value]),
    styles: { fontSize: 7, font: "helvetica" },
    headStyles: { fillColor: [30, 64, 175], textColor: 255 },
    margin: { left: 150, right: 14 },
    tableWidth: 120,
  });

  let y = Math.max(doc.lastAutoTable.finalY, 22) + 8;
  if (tenure?.length) {
    drawBars(doc, {
      x: 14,
      y,
      w: 130,
      h: 50,
      labels: tenure.map((t) => t.label),
      values: tenure.map((t) => t.value),
      title: "Tenure mix (active)",
      color: [124, 58, 237],
    });
  }
  if (movement?.labels?.length) {
    drawBars(doc, {
      x: 155,
      y,
      w: 130,
      h: 50,
      labels: movement.labels,
      values: movement.joins,
      title: `Joins ${movement.year}`,
      color: [5, 150, 105],
    });
  }

  if (compare?.length) {
    doc.addPage();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(30, 41, 59);
    doc.text("Comparison summary", 14, 16);
    autoTable(doc, {
      startY: 20,
      head: [["Month", "Active", "Joins", "Exits", "Net", "Men", "Women", "Probation"]],
      body: compare.map((c) => [
        c.payMonth,
        c.active,
        c.joins,
        c.exits,
        c.netChange,
        c.male,
        c.female,
        c.probation,
      ]),
      styles: { fontSize: 7, font: "helvetica" },
      headStyles: { fillColor: [180, 83, 9], textColor: 255 },
      margin: { left: 14, right: 14 },
    });
    drawBars(doc, {
      x: 14,
      y: doc.lastAutoTable.finalY + 10,
      w: 260,
      h: 55,
      labels: compare.map((c) => c.payMonth),
      values: compare.map((c) => c.active),
      title: "Active headcount by compare month",
    });
  }

  if (alerts?.length) {
    doc.addPage();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(30, 41, 59);
    doc.text("Alerts", 14, 16);
    autoTable(doc, {
      startY: 20,
      head: [["Title", "Detail"]],
      body: alerts.map((a) => [safeText(a.title), safeText(a.detail)]),
      styles: { fontSize: 7, font: "helvetica" },
      headStyles: { fillColor: [185, 28, 28], textColor: 255 },
      margin: { left: 14, right: 14 },
    });
  }

  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i += 1) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(100, 116, 139);
    doc.text("PaceHRM Workforce Dashboard - roster analytics (read-only).", 14, 200);
    doc.text(`Page ${i}/${pages}`, 280, 200, { align: "right" });
  }

  const filename = `Workforce_Dashboard_${String(periodLabel || "export")
    .replace(/[^a-zA-Z0-9_]+/g, "_")
    .slice(0, 40)}_${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
  return filename;
}

export function exportAttendanceDashboardPdf({
  metrics = {},
  compare = [],
  processRows = [],
  periodLabel = "",
} = {}) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  doc.setFillColor(15, 118, 110);
  doc.rect(0, 0, 297, 16, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text("Attendance Dashboard", 14, 10);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(safeText(`${periodLabel || metrics.periodLabel || ""} | ${new Date().toLocaleString("en-IN")}`), 14, 14);

  autoTable(doc, {
    startY: 22,
    head: [["Metric", "Value"]],
    body: [
      ["Headcount", metrics.headcount],
      ["With data", metrics.employeesWithData],
      ["Present days", metrics.totalPresentDays],
      ["Absent/LOP days", metrics.totalAbsentDays],
      ["Leave days", metrics.totalLeaveDays],
      ["WO days", metrics.totalWoDays],
      ["PH days", metrics.totalPhDays],
      ["Present rate %", metrics.presentRate],
      ["Absenteeism %", metrics.absenteeismRate],
      ["Pending leave req", metrics.pendingLeaveRequests],
    ],
    styles: { fontSize: 7, font: "helvetica" },
    headStyles: { fillColor: [15, 118, 110], textColor: 255 },
    margin: { left: 14, right: 160 },
    tableWidth: 110,
  });

  autoTable(doc, {
    startY: 22,
    head: [["Dept (absent)", "Days"]],
    body: (metrics.topAbsentDepts || []).map((d) => [safeText(d.name), d.count]),
    styles: { fontSize: 7, font: "helvetica" },
    headStyles: { fillColor: [220, 38, 38], textColor: 255 },
    margin: { left: 150, right: 14 },
    tableWidth: 120,
  });

  let y = Math.max(doc.lastAutoTable.finalY, 22) + 8;
  drawBars(doc, {
    x: 14,
    y,
    w: 130,
    h: 50,
    labels: (metrics.statusMixChart || []).map((d) => d.label),
    values: (metrics.statusMixChart || []).map((d) => d.value),
    title: "Status mix (days)",
    color: [15, 118, 110],
  });
  drawBars(doc, {
    x: 155,
    y,
    w: 130,
    h: 50,
    labels: (metrics.leaveChart || []).slice(0, 8).map((d) => d.label),
    values: (metrics.leaveChart || []).slice(0, 8).map((d) => d.value),
    title: "Leave type mix",
    color: [29, 78, 216],
  });

  if (compare?.length || processRows?.length) {
    doc.addPage();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(30, 41, 59);
    doc.text("Comparison / process status", 14, 16);
    const rows = compare.length
      ? compare
      : processRows;
    autoTable(doc, {
      startY: 20,
      head: [["Month", "Scope", "Processed", "Open", "Locked", "Present%", "Absent days"]],
      body: rows.map((r) => [
        r.payMonth || r.label,
        r.attendanceScope ?? r.scope ?? "",
        r.attendanceProcessed ?? r.processed ?? "",
        r.attendanceUnprocessed ?? r.open ?? "",
        r.attendanceMonthLocked || r.locked ? "Yes" : "No",
        r.presentRate ?? "",
        r.totalAbsentDays ?? r.absent ?? "",
      ]),
      styles: { fontSize: 7, font: "helvetica" },
      headStyles: { fillColor: [180, 83, 9], textColor: 255 },
      margin: { left: 14, right: 14 },
    });
  }

  if (metrics.alerts?.length) {
    doc.addPage();
    autoTable(doc, {
      startY: 20,
      head: [["Alert", "Detail"]],
      body: metrics.alerts.map((a) => [safeText(a.title), safeText(a.detail)]),
      styles: { fontSize: 7, font: "helvetica" },
      headStyles: { fillColor: [185, 28, 28], textColor: 255 },
      margin: { left: 14, right: 14 },
    });
  }

  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i += 1) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(100, 116, 139);
    doc.text(
      "PaceHRM Attendance Dashboard - read-only from loaded grid / process status. Payable days not recalculated.",
      14,
      200
    );
    doc.text(`Page ${i}/${pages}`, 280, 200, { align: "right" });
  }

  const filename = `Attendance_Dashboard_${String(periodLabel || "export")
    .replace(/[^a-zA-Z0-9_]+/g, "_")
    .slice(0, 40)}_${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
  return filename;
}

export function exportWorkforceCardPdf(detail) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  doc.setFillColor(15, 118, 110);
  doc.rect(0, 0, 210, 14, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(safeText(detail?.title || "Card"), 14, 9);
  autoTable(doc, {
    startY: 20,
    head: [["Field", "Value"]],
    body: (detail?.summary || []).map(([k, v]) => [safeText(k), safeText(v)]),
    styles: { fontSize: 9, font: "helvetica" },
    headStyles: { fillColor: [15, 118, 110], textColor: 255 },
    margin: { left: 14, right: 14 },
  });
  if (detail?.note) {
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(safeText(detail.note), 14, doc.lastAutoTable.finalY + 10, { maxWidth: 180 });
  }
  const filename = `Dashboard_Card_${String(detail?.title || "detail")
    .replace(/[^a-zA-Z0-9_]+/g, "_")
    .slice(0, 30)}_${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
  return filename;
}
